import { Link } from '@tanstack/react-router'
import { AlertTriangle, Truck } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useMyProjectsFilter } from '@/hooks/use-my-projects-filter'
import { segments, type HomeSummary, type Segment } from './model'


export function StatusBars({ summary }: { summary: HomeSummary }) {
  const projects = segments(summary.project_statuses ?? [])
  const purchaseOrders = segments(summary.purchase_order_statuses ?? [])
  const pipeline = summary.pipeline
  const openActions = pipeline?.open_actions ?? 0
  const inTransit = pipeline?.shipments_in_transit ?? 0

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Where projects stand</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <StatusBar title="Active projects" to="/projects" segments={projects} />
        <StatusBar
          title="Purchase orders"
          to="/purchase-orders"
          segments={purchaseOrders}
          extra={
            <>
              {openActions > 0 ? (
                <Link to="/projects/action-items">
                  <Badge variant="destructive">
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    {openActions} action{openActions === 1 ? '' : 's'} needed
                  </Badge>
                </Link>
              ) : null}
              {inTransit > 0 ? (
                <Link to="/shipments">
                  <Badge variant="secondary">
                    <Truck className="mr-1 h-3 w-3" />
                    {inTransit} in transit
                  </Badge>
                </Link>
              ) : null}
            </>
          }
        />
      </CardContent>
    </Card>
  )
}

export function StatusBar({
  title,
  to,
  segments,
  extra,
}: {
  title: string
  to?: '/projects' | '/purchase-orders'
  segments: Segment[]
  extra?: React.ReactNode
}) {
  const { setShowMyProjects } = useMyProjectsFilter()
  const total = segments.reduce((sum, segment) => sum + segment.count, 0)
  const shown = segments.filter((segment) => segment.count > 0)
  const noun = title.toLowerCase()
  // A status opens its list filtered to it. The homepage counts the whole business, so the list
  // switches to All for its count to match.
  const segmentLink = (segment: Segment) =>
    to
      ? ({
          to,
          search: { stage: segment.key },
          onClick: () => setShowMyProjects(false),
        } as const)
      : null
  return (
    <section aria-label={title}>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {to ? (
          <Link to={to} className="text-sm font-semibold hover:underline">
            {title}
          </Link>
        ) : (
          <h2 className="text-sm font-semibold">{title}</h2>
        )}
        <span className="text-xs text-muted-foreground">{total}</span>
        <div className="flex flex-wrap gap-1.5 sm:ml-auto">{extra}</div>
      </div>
      {total > 0 ? (
        <TooltipProvider>
          <div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-muted">
            {shown.map((segment) => {
              const link = segmentLink(segment)
              const width = `${(segment.count / total) * 100}%`
              return (
                <Tooltip key={segment.key}>
                  <TooltipTrigger asChild>
                    {link ? (
                      <Link
                        {...link}
                        className="h-full outline-none transition-opacity first:rounded-l-full last:rounded-r-full hover:opacity-80 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        style={{ width, backgroundColor: segment.color }}
                        aria-label={`${segment.label}: ${segment.count} ${noun}`}
                      />
                    ) : (
                      <div
                        className="h-full first:rounded-l-full last:rounded-r-full"
                        style={{ width, backgroundColor: segment.color }}
                        aria-label={`${segment.label}: ${segment.count}`}
                      />
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    {segment.label}: {segment.count} ({Math.round((segment.count / total) * 100)}%)
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </TooltipProvider>
      ) : (
        <div className="h-3 w-full rounded-full bg-muted" />
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
        {shown.map((segment) => {
          const link = segmentLink(segment)
          const content = (
            <>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
              <span className="text-muted-foreground group-hover:text-foreground">{segment.label}</span>
              <span className="font-medium tabular-nums">{segment.count}</span>
            </>
          )
          return link ? (
            <Link
              key={segment.key}
              {...link}
              className="group -my-1 flex items-center gap-1.5 rounded-sm py-1 text-xs outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`${segment.label}: ${segment.count} ${noun}`}
            >
              {content}
            </Link>
          ) : (
            <div key={segment.key} className="flex items-center gap-1.5 text-xs">
              {content}
            </div>
          )
        })}
      </div>
    </section>
  )
}
